import { RotateCcw, Send, Undo2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { progressPercent } from '../../../../components/progressPercent';
import { formatMoney } from '../../../../lib/formatMoney';
import { CancelPurchaseRequestModal } from '../components/CancelPurchaseRequestModal';
import { PurchaseRequestModal } from '../components/PurchaseRequestModal';
import { purchaseRequestsApi } from '../purchaseRequestsApi';
import { PR_STATUS, fulfilmentOf } from '../purchaseRequestTypes';
import type { PurchaseRequestListItem } from '../purchaseRequestTypes';

/**
 * The purchase-request register.
 *
 * **There is no Clear data**, and the absence is its own decision: emptying a register of what
 * the organization asked to buy and what was approved destroys the accountability record — the
 * call `stock-movements`, `issuances` and `real-properties` already take one suite along.
 */
export function PurchaseRequestsIndexPage() {
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState<PurchaseRequestListItem | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'delete' | 'restore' | 'submit';
    row: PurchaseRequestListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      purchaseRequestsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
          urgency: query.facets.urgency?.join(','),
          facility: query.facets.facility?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (row: PurchaseRequestListItem): MenuItem[] => [
      {
        key: `submit-${row.id}`,
        label: 'Submit for approval',
        icon: <Send className="h-4 w-4" aria-hidden="true" />,
        permission: 'purchase-requests.submit',
        // Disabled for STATE with the reason — the reader holds the key, so a hidden entry
        // would say they lack it.
        disabledReason:
          row.status === PR_STATUS.draft ? null : 'Only a draft can be submitted for approval.',
        onClick: () => setConfirming({ kind: 'submit', row }),
      },
      {
        key: `cancel-${row.id}`,
        label: 'Cancel request',
        icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
        permission: 'purchase-requests.submit',
        disabledReason:
          row.status === PR_STATUS.cancelled ? 'This request has already been cancelled.' : null,
        onClick: () => setCancelling(row),
      },
      {
        key: `restore-${row.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'purchase-requests.delete',
        disabledReason: row.isActive ? 'This request is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', row }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await purchaseRequestsApi.remove(confirming.row.id);
      else if (confirming.kind === 'restore') await purchaseRequestsApi.restore(confirming.row.id);
      else await purchaseRequestsApi.submit(confirming.row.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<PurchaseRequestListItem>
        module="purchase-requests"
        breadcrumb={['Procurement', 'Purchasing', 'Purchase Requests']}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'stage', label: 'Stage' },
          { key: 'urgency', label: 'Urgency' },
          { key: 'facility', label: 'Deliver to' },
        ]}
        /*
          FILED BY STAGE. A reader opens this register to separate the drafts from what is
          waiting on an approver and what has been decided; `status` stays in the rail with Show
          deleted, where it is on every list in the product.
        */
        folderNavFacet="stage"
        auditEntityTypes={['PurchaseRequest', 'PurchaseRequestItem']}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search by PR number, item or justification"
        emptyMessage="No purchase request yet. Raise one when something needs buying."
        onCreate={() => setCreating(true)}
        createPermission="purchase-requests.create"
        // The anchor opens the profile. The READ key — opening a record is not changing it.
        onRowOpen={(row) => navigate(`/procurement/purchasing/purchase-requests/${row.id}`)}
        openPermission="purchase-requests.read"
        rowActions={rowActions}
        /*
          THE FULFILMENT LIST FORMAT — a ring before the kebab, reading how much of the request
          has actually ARRIVED. Product call, 01 Oct 2026 for the format; 05 Oct 2026 for what
          it measures, in the reader’s own words: *the % should indicate whether the items
          were already been received in the Goods receipt.*

          SO THE ROW STATES TWO DIFFERENT DIMENSIONS, which is what makes the pair worth
          having: the STAGE badge is driven by what has been ORDERED — `fully ordered` is
          `Complete`’s own label — and the ring is what has been received against it. A ring
          over ordering would have been the stage column drawn twice.

          `fulfilmentOf` returns null until the request is APPROVED, so the drafts and the
          cancellations beside it draw nothing rather than an empty circle reading 0% — they are
          outside the question rather than at the start of it. The table states the same
          percentage in its `Fulfilled` column, off this same derivation.
        */
        rowProgress={(row) => {
          const progress = fulfilmentOf(row);

          return progress
            ? { value: progress.done, total: progress.total, label: 'units received' }
            : null;
        }}
        onDelete={(row) => setConfirming({ kind: 'delete', row })}
        deletePermission="purchase-requests.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(row) => (row.isActive ? null : 'This request is already deleted.')}
        otherInfo={(row) => row}
      />

      {creating ? (
        <PurchaseRequestModal
          open
          editing={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      ) : null}

      {cancelling ? (
        <CancelPurchaseRequestModal
          open
          request={cancelling}
          isSubmitted={cancelling.status === PR_STATUS.submitted}
          onClose={() => setCancelling(null)}
          onCancelled={() => {
            setCancelling(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={TITLES[confirming.kind]}
          danger={confirming.kind === 'delete'}
          // Both safeguards on the destructive half only. Submitting and restoring need none.
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`purchase-request-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {confirming.kind === 'submit' ? (
                <>
                  <p>
                    {confirming.row.requestNumber} is routed to whoever signs off on purchase
                    requests. It stops being editable at that point.
                  </p>
                  <p>
                    If nobody has been configured as an approver, it goes to the administrators
                    and any one of them can approve it.
                  </p>
                </>
              ) : null}

              {/*
                DELETING IS NOT WITHDRAWING, and the two are the most confusable states here.
                The dialog says so outright and points a reader who reached for the wrong one at
                the other — the disciplinary record's own dialog takes this exact shape.
              */}
              {confirming.kind === 'delete' ? (
                <>
                  <p>
                    {confirming.row.requestNumber} is hidden from the register. Any approval still
                    pending on it is closed.
                  </p>
                  <p>
                    <strong>This is not the same as cancelling it.</strong> Deleting is for a
                    request keyed against the wrong record, or twice. If the organization decided
                    not to buy this, use <strong>Cancel request</strong> instead — that is an outcome
                    the register keeps.
                  </p>
                  <p>Nothing is erased — a deleted request can be restored from this list.</p>
                </>
              ) : null}

              {confirming.kind === 'restore' ? (
                <p>{confirming.row.requestNumber} returns to the register with its lines intact.</p>
              ) : null}

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const TITLES = {
  delete: 'Delete purchase request',
  restore: 'Restore purchase request',
  submit: 'Submit for approval',
} as const;

const COLUMNS: ColumnDef<PurchaseRequestListItem>[] = [
  {
    /*
      THE PR NUMBER is the anchor, because that is what a request is called and quoted by —
      and unlike the item catalog's SKU there is no competing name for the row.
    */
    key: 'requestNumber',
    header: 'PR number',

    /*
      THE ANCHOR CARRIES THE PRODUCTS TOO — product call, 29 Sep 2026, in the reader's own
      words: *concat the items in the PR number. apply max width as well.*

      A register of `PR-26-00006` rows says which request without saying which request is
      which, so a reader had to open one to find out what it was for. This is the two-value
      anchor the education panel already takes — ONE line, ONE header, ONE width, joined by the
      ` — ` this product joins a qualifier to a name with.

      `clamp` is the max width: the joined value has spaces, and `<TextCell>` wraps a multi-word
      value by default — which would make this row taller than the forty around it and cost the
      table the horizontal line it is scanned along. Clamping always attaches the full value as
      a tooltip, so nothing is unreachable.

      THE NUMBER STAYS `mono` AND THE PRODUCTS DO NOT — and that is why the two are one string
      rather than a stacked cell: a `<StackedCell secondary>` is refused in an index table, and
      a second column would print the products a screen away from the request that asks for
      them. One face over the pair is the cost, and the number is still the value a reader
      recognises at the head of the line.
    */
    width: 340,
    sortable: true,
    render: (row) => (
      <TextCell code={row.requestNumber} value={row.items} clamp />
    ),
  },
  {
    key: 'urgency',
    header: 'Urgency',
    width: 120,
    sortable: true,
    summary: true,
    /*
      PLAIN TEXT, not a pill. `pending` is refused on an index table because amber is the brand
      colour, and `blocked` means a state that stops the record being used — which Emergency
      does not. A tone for it would be decoration competing with the row's own state column.
    */
    render: (row) => <TextCell value={row.urgencyLabel} />,
  },
  {
    key: 'categories',
    header: 'Categories',
    width: 200,
    summary: true,
    render: (row) => <TextCell value={row.categories} />,
  },
  {
    key: 'lineCount',
    header: 'Items',
    width: 90,
    align: 'right',
    /*
      NOT a summary value: the list view concatenates its summary values with no labels, so a
      count arrives as a bare `· 2` that a reader cannot interpret without this header.
    */
    render: (row) => <TextCell value={String(row.lineCount)} mono numeric />,
  },
  {
    key: 'totalQuantity',
    header: 'Qty',
    width: 90,
    align: 'right',
    sortable: true,
    render: (row) => <TextCell value={String(row.totalQuantity)} mono numeric />,
  },
  {
    key: 'fulfilled',
    header: 'Fulfilled',
    width: 100,
    align: 'right',
    /*
      THE TABLE'S HALF OF THE FULFILMENT FORMAT. The list row draws this as a ring before its
      kebab; a table row is one line tall and its trailing edge is a pinned 56px gutter, so the
      ring cannot go there — and without this column the list would state a fact the table
      cannot, which is what reference §3 refuses.

      ONE derivation behind both: `fulfilmentOf` decides whether the question applies and
      `progressPercent` rounds it, so the column and the ring cannot answer one request
      differently.

      AN EM DASH where fulfilment is not yet a question — a draft, one awaiting a decision, one
      rejected or cancelled. That is not 0%: zero would claim nothing has arrived against a
      record nothing CAN be delivered against, which is the null-versus-zero distinction the
      Est. total column one over already draws.

      AND `numeric`, so the 0% DIMS — a percentage is a quantity, which is the word the rule
      names, and `isZeroQuantity` names `0%` in its own doc comment. It shipped without for an
      hour: an approved request with nothing received read at full strength beside the muted
      zeros of the Qty column one over, so the one column holding the fact a buyer scans for
      was the one that did not let the eye skip its empties. The two states stay distinct —
      the dash says the question does not apply, the muted zero says the answer is none.

      NOT sortable: the server's allow-list does not name it, and a header that silently fell
      back to the default sort reads as a broken control.
    */
    render: (row) => {
      const progress = fulfilmentOf(row);

      return (
        <TextCell
          value={progress ? `${progressPercent(progress.done, progress.total)}%` : null}
          mono
          numeric
        />
      );
    },
  },
  {
    key: 'estimatedTotal',
    header: 'Est. total',
    width: 140,
    align: 'right',
    /*
      AN ABSENT AMOUNT IS AN EM DASH, never a zero — they mean different things, and a request
      raised before its quotation arrived has no priced line at all. `formatMoney` renders the
      dash for null; the value is `mono numeric`, so a real zero dims.
    */
    render: (row) => <TextCell value={formatMoney(row.estimatedTotal)} mono numeric />,
  },
  {
    key: 'deliverToName',
    header: 'Deliver to',
    width: 180,
    render: (row) => <TextCell value={row.deliverToName} />,
  },
  {
    /*
      THE LIFECYCLE AS PLAIN TEXT, and the row's own live-or-deleted state as the pill below.
      TWO state columns deliberately — the disciplinary panel's split: a deleted request and a
      cancelled one are different facts, and a reader must not have to guess which the column
      is reporting.
    */
    key: 'stage',
    header: 'Stage',
    width: 130,
    sortable: true,
    /*
      THE APPROVAL STATUS, BESIDE `active` — product call, 29 Sep 2026. The flag moves this
      column to sit immediately before the status pill and puts it beside the badge on a list
      row; a purchase request goes through the approval engine, so what the reader wants at a
      glance is where it has got to AND whether the row is live.
    */
    stage: true,
    render: (row) => <StatusText tone="inactive">{row.statusLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (row) =>
      row.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
